/**
 * 文件作用：集中式模型配置（Provider-Model 分层架构）
 * - 统一声明每个模型的 providerId、默认任务类型、UI 模式与适配器
 * - 通过 findModelConfig 等工具函数，其他模块可快速获取模型元信息，避免多处硬编码
 * - 新增模型只需：
 *   1. 新增适配器文件并在此处引入
 *   2. 添加一条 ModelConfigEntry（指定 providerId 等）
 * - Provider 层配置见 providers.config.ts
 */
import type { GenerationAdapter, TaskType } from '../adapters/types';
import type { ModelWorkspaceConfig } from '@/features/workspace/modelFormTypes';
import { discoverModels } from './modelAutoDiscovery';
import type { ProviderId } from './providers.config';
import type { CanonicalFieldKey } from '@/features/canvas/nodes/model/canonicalPorts';

export type ModelMode = 'text' | 'image' | 'video';

/**
 * 模型角色类型
 * - generator: 生成模型，用于生成内容（显示在 Create 页面和 Canvas）
 * - processor: 处理节点，用于处理/转换数据（仅显示在 Canvas）
 */
export type ModelRole = 'generator' | 'processor';

export interface VersionOption {
  value: string;
  label: string; // label 通常保存 schema 中的 labelKey，交由 UI 层翻译
  /** 是否在 UI 中可见（默认 true） */
  visible?: boolean;
}

export interface ModelPollingConfig {
  enabled?: boolean;
  /**
   * 同步模型非阻塞执行
   * - 当设置为 true 时，同步模型的 API 调用将在后台执行，不阻塞用户界面
   * - 用户可连续点击生成多张图片，无需等待上一张生成完成
   * - 与 enabled 的区别：enabled 用于真正需要轮询的异步模型（如 Kling、Seedance）
   */
  nonBlocking?: boolean;
  statusEndpointTemplate?: string;
  /**
   * 任务成功的判定字段（单一路径，保留向后兼容）
   */
  successPath?: string;
  /**
   * 任务成功的候选字段路径列表（优先于 successPath，依次尝试）
   */
  successFieldPaths?: string[];
  /**
   * 状态字段的成功值列表
   * 当 successFieldPaths 包含 'status' 等状态路径时，需配合此字段指定哪些值表示成功
   * 例如: ['succeeded', 'completed', 'done']
   * 若不配置，则该路径只要有非空值即视为成功（向后兼容）
   */
  successStatuses?: string[];
  progressPath?: string;
  statusFieldPaths?: string[];
  failureStatuses?: string[];
  failureMessagePaths?: string[];
}

export interface ModelCopyLocalization {
  labelKey?: string;
  descriptionKey?: string;
  seoTitleKey?: string;
  seoDescriptionKey?: string;
  seoKeywordsKey?: string;
  productNameKey?: string;
}

/**
 * 模型调用历史缓存的基础结构
 * - input: 记录提交表单时的关键信息
 * - output: 记录模型返回的原始响应，便于复现或调试
 */
export interface ModelHistoryInputSnapshot {
  prompt: string;
  parameters: Record<string, unknown>;
  createdAt: string; // ISO 8601 时间戳，便于序列化到本地缓存
}

export interface ModelHistoryOutputSnapshot {
  rawResponse: unknown;
}

export interface ModelHistoryCacheEntry {
  modelKey: string; // 对应的模型标识，便于按模型维度筛选历史记录
  input: ModelHistoryInputSnapshot;
  output?: ModelHistoryOutputSnapshot; // 有些任务可能仍在处理中
}

export type ModelHistoryCache = ModelHistoryCacheEntry[];

/** 画板节点端口配置 */
export interface NodeGraphPortConfig {
  /** 端口 ID */
  id: string;
  /** 端口显示名称 */
  name: string;
  /** 端口数据类型 */
  dataType: 'text' | 'image' | 'video' | 'audio' | 'url' | 'json' | 'any';
  /** 对应的表单字段 key */
  fieldKey: string;
  /** 对应的 canonical 字段（可选，用于跨节点对齐语义） */
  canonicalKey?: CanonicalFieldKey;
  /** 端口描述 */
  description?: string;
  /** 是否必需 */
  required?: boolean;
  /** 是否允许多连接/多值 */
  multiple?: boolean;
}

export interface CanvasPortFieldSpec {
  /** 对应表单字段 key（实际存储字段） */
  fieldKey: string;
  /** 对应的 canonical 字段（用于校验/推导），可省略自动按 alias 解析 */
  canonicalKey?: CanonicalFieldKey;
  /** 端口 ID 覆盖（默认使用 canonical portId） */
  portId?: string;
  /** 端口数据类型覆盖（默认取 canonical） */
  dataType?: NodeGraphPortConfig['dataType'];
  /** 是否必填 */
  required?: boolean;
  /** 是否允许多连/多值 */
  multiple?: boolean;
  /** 端口描述 */
  description?: string;
  /** 默认值（优先于 canonical 默认值） */
  defaultValue?: unknown;
}

export interface CanvasPortSpec {
  inputs: CanvasPortFieldSpec[];
}

/**
 * 视频生成模式单项配置
 * - labelKey: i18n 翻译键
 * - color: 可选的状态颜色
 */
export interface VideoGenModeLabelConfig {
  labelKey: string;
  color?: string;
}

/**
 * 视频生成模式配置
 * 用于在 Canvas 节点属性面板中显示当前视频生成模式（文生视频/首帧模式/首尾帧模式）
 */
export interface VideoGenModeConfig {
  /** 是否启用模式指示器 */
  enabled: true;
  /** 图片输入端口的 fieldKey（用于计算连接的图片数量） */
  imageFieldKey: string;
  /** 各模式配置，key 为图片数量 */
  modes: {
    /** 无图片输入：文生视频模式 */
    0: VideoGenModeLabelConfig;
    /** 1张图片：首帧模式 */
    1: VideoGenModeLabelConfig;
    /** 2张图片：首尾帧模式 */
    2: VideoGenModeLabelConfig;
  };
}

export interface ModelConfigEntry {
  providerId: ProviderId;      // AI 厂商标识（关联 providers.config.ts）
  /**
   * 可选：模型所属分组 ID（关联 modelGroups.config.ts）
   * - 未设置时默认使用 providerId
   * - 允许自定义分组（如跨 provider 的推荐模型）
   */
  groupId?: string;
  label: string;               // UI 展示名称
  modelKey: string;            // 任务系统内使用的 key（kebab-case）
  defaultTaskType: TaskType;   // 默认任务类型
  mode: ModelMode;             // UI 默认模式（Create 页面使用）
  adapter: GenerationAdapter;  // 对应的适配器实例
  defaults?: Record<string, unknown>; // 可选：请求默认值（contentType/timeout/retries 等）
  visible?: boolean;           // 可选：是否在模型选择器中可见（默认 true）
  role?: ModelRole;            // 可选：模型角色（默认 generator）
  description?: string;        // 可选：模型描述
  aliases?: string[];          // 可选：额外可用于查找的别名
  versions?: VersionOption[];  // 可选：版本列表（从 schema 静态解析，供 Dock 快速索引）
  routeSegment?: string;       // 可选：在路由中使用的 slug（默认等于 modelKey）
  workspace?: ModelWorkspaceConfig;
  polling?: ModelPollingConfig;
  seo?: ModelSeoConfig;
  /** 画板端口字段声明（无 nodeGraphPorts 时按此生成） */
  canvasPortSpec?: CanvasPortSpec;
  /**
   * 可选：按版本（通常为表单字段 `model`）提供差异化 canvas 端口。
   * - key 为版本值（如 `doubao-seedance-1-0-pro-fast-251015`）
   * - 支持提供 `default` 作为兜底
   * - 当命中该表时，优先使用其 inputs 生成节点端口
   */
  canvasPortSpecByVersion?: Record<string, CanvasPortSpec>;
  i18n?: ModelCopyLocalization;
  nodeGraphPorts?: {
    inputs?: NodeGraphPortConfig[];
    outputs?: NodeGraphPortConfig[];
  };
  /** 视频生成模式配置（仅适用于支持图片输入的视频模型） */
  videoGenMode?: VideoGenModeConfig;
}

export interface ModelSeoConfig {
  image?: string;
  twitterImage?: string;
}

// 手动配置已迁移为自动发现，保留空数组用于兼容旧引用
const manualModelsConfig: ModelConfigEntry[] = [];

let autoDiscoveredModels: ModelConfigEntry[] = [];
let discoveryPromise: Promise<void> | null = null;

const getAllConfigs = (): ModelConfigEntry[] => {
  const map = new Map<string, ModelConfigEntry>();
  manualModelsConfig.forEach((cfg) => map.set(cfg.modelKey, cfg));
  autoDiscoveredModels.forEach((cfg) => map.set(cfg.modelKey, cfg));
  return Array.from(map.values());
};

type LookupMap = Map<string, ModelConfigEntry>;

/**
 * 构建查找索引，支持多种查找方式：
 * - modelKey（主键）
 * - routeSegment
 * - providerId（厂商ID）
 * - aliases（别名）
 */
const buildLookup = (configs: ModelConfigEntry[]): LookupMap => {
  const map: LookupMap = new Map();

  configs.forEach((entry) => {
    const candidates = new Set<string>();

    // 主键：modelKey 永远是 canonical key
    candidates.add(entry.modelKey);

    // 路由段
    if (entry.routeSegment) {
      candidates.add(entry.routeSegment);
    }

    // Provider 维度：使用 providerId
    candidates.add(entry.providerId);

    // 配置内声明的别名
    (entry.aliases ?? []).forEach((alias) => {
      if (alias) {
        candidates.add(alias);
      }
    });

    // 写入索引（normalize 为小写）
    candidates.forEach((rawKey) => {
      const key = rawKey.trim().toLowerCase();
      if (!key) return;
      if (!map.has(key)) {
        map.set(key, entry);
      }
    });
  });

  return map;
};

let lookup: LookupMap | null = null;

const refreshLookup = (): void => {
  lookup = buildLookup(getAllConfigs());
};

const ensureLookup = (): LookupMap => {
  if (!lookup) {
    refreshLookup();
  }
  // 非空断言：refresh 后必然存在
  return lookup!;
};

export async function initAutoDiscovery(): Promise<void> {
  // 避免重复加载与并发触发（边缘运行时会重复实例化，但同一实例内只加载一次）
  if (autoDiscoveredModels.length > 0) return;
  if (discoveryPromise) return discoveryPromise;

  discoveryPromise = (async () => {
    try {
      const discovered = await discoverModels();
      autoDiscoveredModels = discovered;
      refreshLookup();
    } catch {
      // 忽略自动发现失败
    } finally {
      // 允许后续重试（例如首轮失败时）
      discoveryPromise = null;
    }
  })();

  return discoveryPromise;
}

/**
 * 模型配置查找函数
 * - 支持：modelKey / routeSegment / alias / providerId
 * - 行为等价于旧版 findModelConfig，但底层实现统一走 canonical modelKey
 *
 * @param identifier - 模型标识符（modelKey / providerId / alias / routeSegment）
 * @returns 模型配置对象，未找到时返回 undefined
 *
 * @example
 * findModelConfig('sora-2')           // 通过 modelKey 查找
 * findModelConfig('openai')           // 通过 providerId 查找
 * findModelConfig('veo')              // Google VEO 模型
 */
export const findModelConfig = (identifier?: string | null): ModelConfigEntry | undefined => {
  if (!identifier) return undefined;
  const key = identifier.trim().toLowerCase();
  if (!key) return undefined;
  return ensureLookup().get(key);
};

/**
 * 提供所有模型配置的浅拷贝，避免外部意外修改原始数组
 */
export const listModelConfigs = (): ModelConfigEntry[] => getAllConfigs();

export const listVisibleModelConfigs = (): ModelConfigEntry[] =>
  listModelConfigs().filter((config) => config.visible !== false);

/**
 * 获取所有可见的生成模型配置
 * - 用于 Create 页面模型选择器、首页展示等
 * - 仅包含 role === 'generator' 或未设置 role 的模型
 */
export const listGeneratorConfigs = (): ModelConfigEntry[] =>
  listModelConfigs().filter(
    (config) => config.visible !== false && (config.role === 'generator' || config.role === undefined)
  );

/**
 * 获取所有可见的处理节点配置
 * - 用于 Canvas 节点面板中的处理工具分类
 * - 仅包含 role === 'processor' 的模型
 */
export const listProcessorConfigs = (): ModelConfigEntry[] =>
  listModelConfigs().filter((config) => config.visible !== false && config.role === 'processor');

export const listModelConfigsWithSource = (): { manual: ModelConfigEntry[]; auto: ModelConfigEntry[] } => ({
  manual: [...manualModelsConfig],
  auto: [...autoDiscoveredModels],
});

export const getModelRouteSegment = (config: ModelConfigEntry): string =>
  config.routeSegment ?? config.modelKey;

export interface ModelRouteEntry {
  config: ModelConfigEntry;
  segment: string;
}

export const getVisibleModelRouteEntries = (): ModelRouteEntry[] =>
  listVisibleModelConfigs().map((config) => ({
    config,
    segment: getModelRouteSegment(config),
  }));

export const getDefaultModelConfig = (): ModelConfigEntry | undefined =>
  listVisibleModelConfigs()[0];

export const getDefaultModelRouteSegment = (): string | undefined => {
  const config = getDefaultModelConfig();
  return config ? getModelRouteSegment(config) : undefined;
};

export default manualModelsConfig;
